import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Container } from 'reactstrap';
import ReactLoading from 'react-loading';

import { shippingApi } from '../../api/shipping';
import {
  selectAccessSensitive,
  selectHouseID,
  selectIsInvShippingEnabled,
  selectIsUserLoaded
} from '../../selectors';
import './ShippingPayouts.scss';

// Copy per failure mode. `retryable` is the part that matters: a house with no
// Tipalti payee record (404) or a session/permission problem (401/403) will fail
// identically however many times the user clicks, so don't offer the button.
const errorsByStatus = {
  401: {
    retryable: false,
    message: 'Your session has expired. Sign in again to manage your payout details.'
  },
  403: {
    retryable: false,
    message: 'You do not have permission to view payout details for this account.'
  },
  404: {
    retryable: false,
    message: 'Your payout account has not been set up yet. Contact your account manager to get started.'
  }
};

// 502 (Tipalti unreachable or the integration is unconfigured for this
// environment) and network failures land here.
const defaultError = {
  retryable: true,
  message: 'We could not load your payout onboarding right now. Please try again in a few minutes.'
};

const spinner = <ReactLoading className="loading-spinner" type="spin" color="black" />;

// Everything except the loaded iframe gets the usual page chrome; only the
// success state goes full-bleed.
const messagePage = (color, body) => (
  <Container className="mt-4 mb-5">
    <h1>Shipping Payouts</h1>
    <Alert color={color} className="mt-3">
      {body}
    </Alert>
  </Container>
);

// Anchored on a dot so it can't be satisfied by a lookalike domain such as
// evil-tipalti.com, which a bare endsWith('tipalti.com') would accept. Matched on
// the domain rather than an exact host because the host varies by environment
// (ui2.tipalti.com in prod, ui2.sandbox.tipalti.com below it).
const isTipaltiOrigin = origin => typeof origin === 'string'
  && (origin === 'https://tipalti.com' || origin.endsWith('.tipalti.com'));

// The URL comes from our own same-origin service, so this is belt-and-braces:
// it bounds the blast radius if that service ever returns something unexpected,
// rather than pointing the iframe wherever it's told.
const isTipaltiUrl = url => {
  try {
    return isTipaltiOrigin(new URL(url).origin);
  } catch (err) {
    return false;
  }
};

/**
 * Embeds Tipalti's hosted payee-dashboard iframe so a house can complete payout
 * onboarding (bank details, tax forms). shipping-svc mints the HMAC-signed,
 * short-lived iframe URL server-side (the signing key never reaches the browser);
 * here we just fetch it once and render it.
 *
 * The URL is deliberately not refreshed on a timer. Its expiry only bounds how
 * long the URL is valid to *load*, and the load is immediate — and because the
 * backend clamps it to `min(ts + 600, dynamicKey.expiresAt)` against a key it
 * caches for ~10 min, `expiresAt` can legitimately come back seconds away. A
 * naive refresh timer would go negative and hammer the endpoint; re-pointing the
 * iframe would also restart Tipalti's session and lose in-progress form input.
 * Navigating back to the page remounts it and mints a fresh URL.
 */
const ShippingPayouts = () => {
  const houseID = useSelector(selectHouseID);
  const shippingEnabled = useSelector(selectIsInvShippingEnabled);
  const userLoaded = useSelector(selectIsUserLoaded);
  const accessSensitive = useSelector(selectAccessSensitive);

  const [iframeUrl, setIframeUrl] = useState('');
  const [error, setError] = useState(null);

  const loadIframeAuth = useCallback(async signal => {
    if (!houseID) {
      return;
    }

    setIframeUrl('');
    setError(null);

    const { status, data } = await shippingApi.getTipaltiIframeAuth(houseID, signal);

    if (signal && signal.aborted) {
      return;
    }

    const url = data && data.iframeUrl;

    if (!url || !isTipaltiUrl(url)) {
      // Don't log the URL itself — it carries the signed hashkey.
      if (url) {
        console.warn('Ignoring iframe URL that is not a Tipalti origin');
      }
      setError(errorsByStatus[status] || defaultError);
      return;
    }

    setIframeUrl(url);
  }, [houseID]);

  // Only the fully-authorized case gets to make the call — a house without
  // shipping, or a user without sensitive access, must not hit the endpoint.
  const authorized = userLoaded && accessSensitive && shippingEnabled;

  useEffect(() => {
    if (!authorized) {
      return undefined;
    }

    const controller = new AbortController();
    loadIframeAuth(controller.signal);
    return () => controller.abort();
  }, [authorized, loadIframeAuth]);

  // `state.user` populates last in app boot (after the shipping policy), so
  // waiting on it keeps both guards below from flashing before we know anything.
  if (!userLoaded) {
    return spinner;
  }

  if (!accessSensitive) {
    return messagePage(
      'info',
      'You do not have access to payout details. Ask an administrator on your account for access to sensitive data.'
    );
  }

  if (!shippingEnabled) {
    return messagePage(
      'info',
      'Invaluable Shipping is not enabled for your account. Contact your account manager to get started.'
    );
  }

  if (error) {
    return messagePage('danger', (
      <>
        {error.message}
        {error.retryable && (
          <div className="mt-2">
            <Button color="primary" onClick={() => loadIframeAuth()}>
              Retry
            </Button>
          </div>
        )}
      </>
    ));
  }

  if (!iframeUrl) {
    return spinner;
  }

  return (
    <div className="shipping-payouts-iframe-container">
      <iframe title="Tipalti Payee Dashboard" src={iframeUrl} className="iframe" />
    </div>
  );
};

export default ShippingPayouts;
